import { ArrowRight, Building2, UsersRound } from "lucide-react";

export interface WorkspaceSummary {
  id: string;
  name: string;
  status: "active" | "suspended";
  createdLabel?: string;
  createdSortValue?: number;
}

interface WorkspaceCardProps {
  workspace: WorkspaceSummary;
  userCount: number | null;
  userCountState: "ready" | "loading" | "error";
  onOpen: (workspaceId: string) => void;
}

export function WorkspaceCard({
  workspace,
  userCount,
  userCountState,
  onOpen,
}: WorkspaceCardProps) {
  const countLabel =
    userCountState === "loading"
      ? "Loading user records"
      : userCountState === "error"
        ? "User count unavailable"
        : `${userCount ?? 0} user record${userCount === 1 ? "" : "s"} loaded`;

  return (
    <article className="group relative overflow-hidden rounded-2xl border border-[#dfe5df] bg-[#fffefb] p-5 shadow-[0_7px_22px_rgba(23,79,57,0.04)] sm:p-6">
      <div aria-hidden="true" className="absolute -right-16 -top-20 h-48 w-48 rounded-full bg-[#deeee4] transition-transform duration-300 group-hover:scale-105" />
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#174f39] text-white">
          <Building2 className="h-5 w-5" />
        </div>
        <span
          className={
            workspace.status === "active"
              ? "rounded-full bg-[#e0f2e7] px-2.5 py-1 font-body text-[10px] font-extrabold uppercase tracking-wide text-[#246344]"
              : "rounded-full bg-[#fff0cf] px-2.5 py-1 font-body text-[10px] font-extrabold uppercase tracking-wide text-[#815b10]"
          }
        >
          {workspace.status}
        </span>
      </div>
      <div className="relative mt-9">
        <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#277454]">
          Workspace
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-[#17221c]">{workspace.name}</h2>
        <p className="mt-2 font-body text-sm leading-6 text-[#5f6963]">
          Open users, rosters, and available Studio controls.
        </p>
      </div>
      <div className="relative mt-6 flex flex-col gap-3 border-t border-[#e7ebe7] pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 font-body text-xs text-[#647068]">
          <UsersRound className="h-4 w-4" />
          <span>{countLabel}</span>
        </div>
        <button
          type="button"
          onClick={() => onOpen(workspace.id)}
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-[#174f39] px-4 font-heading text-xs font-bold text-white transition hover:bg-[#277454] focus:outline-none focus:ring-2 focus:ring-[#e66f51]/50 focus:ring-offset-2"
        >
          Open workspace
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      {workspace.createdLabel ? (
        <p className="relative mt-3 font-body text-[10px] text-[#849088]">Created {workspace.createdLabel}</p>
      ) : null}
    </article>
  );
}
